import { Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { LeakedSearchTermRow } from 'src/context/api/campaignLeaks/api';
import BarCell from '../shared/bar-cell';
import { formatInteger, formatMoney } from '../shared/format';
import { anyOfOperators, containsOperators, rangeOperators } from '../shared/grid-operators';
import type { CampaignLeaksT } from '../shared/i18n';
import { distinctValues, formatKeyword, getLeakedAccessors, matchTypeLabel } from './utils';

const text = (value: string) => (
  <Typography noWrap sx={{ fontSize: 14, color: '#2F3843' }}>
    {value}
  </Typography>
);

type BuildArgs = {
  ct: CampaignLeaksT;
  rows: LeakedSearchTermRow[];
  currency: string;
  maxCost: number;
  maxClicks: number;
};

export const buildLeakedColumns = ({ ct, rows, currency, maxCost, maxClicks }: BuildArgs): GridColDef<LeakedSearchTermRow>[] => {
  const accessors = getLeakedAccessors(ct);
  const choices = (field: string) => {
    const options = distinctValues(rows, accessors, field);
    return { type: 'singleSelect' as const, valueOptions: options, filterOperators: anyOfOperators(ct, options) };
  };
  return [
    {
      field: 'search_term',
      headerName: ct('columns.search_term'),
      flex: 1.3,
      minWidth: 220,
      filterOperators: containsOperators(),
      renderCell: ({ row }) => text(row.search_term),
    },
    {
      field: 'match_type',
      headerName: ct('columns.match_type'),
      minWidth: 120,
      valueGetter: (_value, row) => matchTypeLabel(ct, row.keyword_match_type),
      ...choices('match_type'),
    },
    { field: 'campaign_type', headerName: ct('columns.campaign_type'), minWidth: 150, ...choices('campaign_type') },
    {
      field: 'region_name',
      headerName: ct('columns.region'),
      minWidth: 120,
      valueGetter: (_value, row) => accessors.region_name(row),
      ...choices('region_name'),
    },
    {
      field: 'account_name',
      headerName: ct('columns.account'),
      minWidth: 160,
      ...choices('account_name'),
      renderCell: ({ row }) => text(row.account_name),
    },
    {
      field: 'campaign_name',
      headerName: ct('columns.campaign_name'),
      flex: 1,
      minWidth: 200,
      ...choices('campaign_name'),
      renderCell: ({ row }) => (
        <Tooltip title={row.campaign_name} placement="top">
          {text(row.campaign_name)}
        </Tooltip>
      ),
    },
    {
      field: 'ad_group_name',
      headerName: ct('columns.ad_group'),
      minWidth: 160,
      ...choices('ad_group_name'),
      renderCell: ({ row }) => text(row.ad_group_name),
    },
    {
      field: 'keyword',
      headerName: ct('columns.keyword'),
      minWidth: 180,
      filterOperators: containsOperators(),
      renderCell: ({ row }) => text(formatKeyword(row.keyword, row.keyword_match_type)),
    },
    {
      field: 'ad_cost',
      headerName: ct('columns.ad_cost'),
      type: 'number',
      headerAlign: 'left',
      minWidth: 230,
      flex: 1,
      filterOperators: rangeOperators(ct),
      renderCell: ({ row }) => <BarCell label={formatMoney(row.ad_cost ?? 0, currency)} value={row.ad_cost ?? 0} max={maxCost} />,
    },
    {
      field: 'ad_clicks',
      headerName: ct('columns.ad_clicks'),
      type: 'number',
      headerAlign: 'left',
      minWidth: 180,
      flex: 0.8,
      filterOperators: rangeOperators(ct),
      renderCell: ({ row }) => <BarCell label={formatInteger(row.ad_clicks ?? 0)} value={row.ad_clicks ?? 0} max={maxClicks} />,
    },
  ];
};
